/* 生年月日ログイン(申込人) */
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import Block from '../../molecules/Block';
import Annotation from '../../atoms/Annotation';
import axios from '../../axios';
import { handleBrowserBack } from '../../BrowserBack';
import LoadingModal from '../../molecules/LoadingModal';
import Heading from '../../molecules/Heading';
import StepNavigation from '../../molecules/StepNavigation';
import Text from '../../molecules/Text';
import { KWANKGM00050A03 } from '../../const/api_modules';

const BirthLogin: React.FC = () => {
  // 入力値を格納する
  const [authentication_code, setAuthenticationCode] = useState('');
  // ローディング画面表示状態
  const [is_loading, setIsLoading] = useState(false);
  // エラーメッセージ
  const [error_authentication_code, setErrorAuthenticationCode] = useState('');

  const node = document.getElementById('application-person-birth-login');
  const token = node?.dataset.token;
  const reception_id = node?.dataset.reception_id;
  const guarantor_sub_no = node?.dataset.guarantor_sub_no;

  // 連帯保証人情報フラグ
  // NOTE: 連帯債務者等のサブ番号がある場合は連帯債務者とする
  const is_guarantor = guarantor_sub_no ? true : false;

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
  }, []);

  // 値入力
  const handleChangeInput = (changed_value: string) => {
    setAuthenticationCode(changed_value);
  };

  /**
   * ログイン
   */
  const handleBirthLoginClick = async () => {
    try {
      setIsLoading(true);
      setErrorAuthenticationCode('');
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/mypage/login/authentication_login`,
        {
          token: token,
          reception_id: reception_id,
          auth_code: authentication_code,
          guarantor_sub_no: guarantor_sub_no,
        }
      );

      // ログインOK
      // 受付CDと受付IDを取得
      const r_cd = response.data.reception_cd;
      const r_id = response.data.reception_id;
      const my_page_ja_code = response.data.my_page_ja_code;
      const status_code = response.data.status_code;
      const is_mypage_opened = response.data.is_mypage_opened;

      // 案件詳細画面へ遷移
      if (is_guarantor) {
        // KWANKGM00050A03へリクエストする
        const api_response = await axios.post(
          `${location.origin}${process.env.MIX_MOCK_URL}/api/common`,
          {
            reception_id: r_id,
            reception_cd: r_cd,
            status: status_code,
            module_name: KWANKGM00050A03,
            detail: [],
            is_json: true,
            email: { val: response.data.email },
            birthday: { val: authentication_code },
          }
        );
        // 処理に失敗している場合はエラーメッセージを表示して処理を終了する
        if (!api_response.data.result) {
          console.error(api_response.data.message);
          setErrorAuthenticationCode(api_response.data.message);
          return;
        }

        // 連帯債務者等の場合
        // マイページ開設済の場合、ログイン画面へ遷移
        if (is_mypage_opened) {
          await axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/logout`);
          location.replace(`${location.origin}${process.env.MIX_MOCK_URL}/mypage/login`);
        }
        // マイページ未開設の場合、マイページ開設・個人情報取扱等同意確認へ遷移
        else {
          location.replace(
            `${location.origin}${process.env.MIX_MOCK_URL}/agreement_solidarity?jacode=${my_page_ja_code}&guarantor_sub_no=${guarantor_sub_no}&r_id=${r_id}&r_cd=${r_cd}`
          );
        }
      } else {
        location.replace(
          `${location.origin}${process.env.MIX_MOCK_URL}/reception/${r_cd}/${r_id}/${status_code}`
        );
      }
    } catch (error) {
      // ログインNG
      console.error(error);
      if (error.response.data.message.auth_code) {
        setErrorAuthenticationCode(error.response.data.message.auth_code);
      } else {
        setErrorAuthenticationCode(error.response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 連帯債務者等による確認の場合かどうかで、ボタンの表示を切り替える
  const displaySendButton = () => {
    // 連帯債務者等による借入申込の内容確認の前、または案件紐づけ時の場合
    if (is_guarantor) {
      return (
        <button
          className="c-btn btn-max btn-green btn-arrow-right mx-auto mb-5"
          type="button"
          onClick={handleBirthLoginClick}
        >
          認証コードを送信する
        </button>
      );
    }

    // 事前審査結果確認の前の場合
    return (
      <button
        className="c-btn btn-max btn-green btn-arrow-right mx-auto mb-5"
        type="button"
        onClick={handleBirthLoginClick}
      >
        事前審査結果を確認する
      </button>
    );
  };

  return (
    <>
      <Heading
        label="借入申込事前準備"
        bold={true}
        size="l"
        css_class={`${is_guarantor ? 'dom-hidden' : ''}`}
      />
      <StepNavigation
        current={1}
        step1="事前審査結果確認"
        step2="マイページ開設"
        step3="完了"
        disabled={is_guarantor}
      />
      <Block
        label="認証コード入力"
        content={
          <>
            <div className="container-box">
              <div>
                {/* 生年月日入力欄 */}
                <Annotation text="認証コードをご入力下さい。" />
                <div className="mt-3">
                  <Text
                    id="authenticate_code"
                    label="認証コード"
                    value={authentication_code}
                    placeholder="例) 19801215"
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      handleChangeInput(event.target.value);
                    }}
                    maxlength={8}
                    disabled={false}
                    help_msg="※半角数字8文字で入力して下さい\n※法人の場合は設立年月日を入力して下さい"
                    message={error_authentication_code}
                    heading="生年月日・設立年月日"
                    right_padding={true}
                  />
                </div>
                <div className="text-center mt-5">{displaySendButton()}</div>
                <LoadingModal show={is_loading} />
              </div>
            </div>
          </>
        }
      />
    </>
  );
};

if (document.getElementById('application-person-birth-login')) {
  ReactDOM.render(<BirthLogin />, document.getElementById('application-person-birth-login'));
}
